import React, { useState, useEffect, useCallback } from 'react';
import { FiTag, FiCheck, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { getUnclassifiedJobs, setJobServiceType } from '../services/api';

// Clear the service-type backlog without a spreadsheet round-trip.
//
// These jobs have real field hours but no type, so they're invisible to every median on
// this page. Sorted biggest-hours-first on purpose: the backlog is heavily skewed, so
// the list is worth abandoning halfway down — the tail will never move a number.
//
// A job classified here vanishes from the list and the counter drops, so it's obvious
// how much is left and whether it's still worth continuing.

export default function UnclassifiedJobs() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(null);
  const [done, setDone] = useState({});     // job id -> what it was set to
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await getUnclassifiedJobs();
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load unclassified jobs');
    }
  }, []);

  useEffect(() => { if (open && !data) load(); }, [open, data, load]);

  const classify = async (job, primary, secondary) => {
    setSaving(job.id); setError('');
    try {
      await setJobServiceType(job.id, primary, secondary || null);
      setDone((d) => ({ ...d, [job.id]: [primary, secondary].filter(Boolean).join(' + ') }));
    } catch (e) {
      setError(e.response?.data?.error || 'That did not save');
    }
    setSaving(null);
  };

  if (!data && !open) {
    return (
      <button onClick={() => setOpen(true)} style={btn}>
        <FiTag /> Classify unclassified jobs <FiChevronDown />
      </button>
    );
  }

  const remaining = (data?.jobs || []).filter((j) => !done[j.id]);
  const clearedHours = (data?.jobs || [])
    .filter((j) => done[j.id])
    .reduce((s, j) => s + j.field_hours, 0);

  return (
    <div style={{ marginTop: 16 }}>
      <button onClick={() => setOpen(false)} style={btn}>
        <FiTag /> Classify unclassified jobs <FiChevronUp />
      </button>

      {!data && <div style={{ padding: 12, color: '#6b7280' }}>Loading…</div>}

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
          borderRadius: 8, padding: '8px 12px', fontSize: 13, margin: '10px 0' }}>{error}</div>
      )}

      {data && (
        <>
          <div style={{ fontSize: 13, color: '#4b5563', margin: '10px 0 12px' }}>
            <strong>{remaining.length}</strong> jobs left
            {clearedHours > 0 && (
              <span style={{ color: '#16a34a', fontWeight: 600 }}>
                {' '}· {Math.round(clearedHours).toLocaleString()} hours brought into the numbers
              </span>
            )}
            . Biggest first — the tail is small enough to skip.
          </div>

          {remaining.length === 0 && (
            <div style={{ color: '#16a34a', fontWeight: 600, padding: '10px 0' }}>
              <FiCheck /> Nothing left with meaningful hours.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 620, overflowY: 'auto' }}>
            {remaining.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                types={data.service_types}
                busy={saving === job.id}
                onSave={(p, s) => classify(job, p, s)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function JobRow({ job, types, busy, onSave }) {
  const [primary, setPrimary] = useState('');
  const [secondary, setSecondary] = useState('');

  return (
    <div style={{
      border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px',
      display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap',
    }}>
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>
          {job.job_number || `(job ${job.id})`}
          <span style={{ marginLeft: 8, fontWeight: 800, color: '#b45309' }}>
            {job.field_hours}h
          </span>
        </div>
        <div style={{ fontSize: 13, color: '#4b5563' }}>
          {job.client_name}{job.property_address ? ` · ${job.property_address}` : ''}
        </div>
        {job.description && (
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{job.description}</div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
        <select value={primary} onChange={(e) => setPrimary(e.target.value)} disabled={busy} style={sel}>
          <option value="">Type…</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {/* Only offered once a primary is chosen — a secondary alone is meaningless,
            and this is for genuinely combined scopes (a boundary that was also a topo),
            not a job that merely touched two things. */}
        {primary && (
          <select value={secondary} onChange={(e) => setSecondary(e.target.value)} disabled={busy} style={sel}>
            <option value="">+ also… (optional)</option>
            {types.filter((t) => t !== primary).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <button
          onClick={() => onSave(primary, secondary)}
          disabled={busy || !primary}
          style={{
            padding: '7px 14px', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13,
            background: primary ? '#16a34a' : '#d1d5db', color: '#fff',
            cursor: primary && !busy ? 'pointer' : 'not-allowed',
          }}
        >
          {busy ? '…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

const btn = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 14px',
  border: '1px solid #d1d5db', background: '#fff', borderRadius: 8,
  cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151',
};

const sel = {
  padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13,
  background: '#fff', cursor: 'pointer',
};
